import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, ConfigService } from '@app-core/services';
import { DecodeVinResponse, ManualResponse } from '@app-model/api';
import { UserResponse } from '@app-model/login';
import { GetPostalCodeResponse } from '@app-model/response';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as VehicleActions from './vehicle.actions';
import { State } from './vehicle.reducer';
import * as fromRoot from '../reducers';

@Injectable()
export class VehicleEffects {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private store = inject<Store<fromRoot.State>>(Store);
  private _configService = inject(ConfigService);

  getVehicleForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_PRODUCT_FORM),
      switchMap(() =>
        this.apiService.getProductForm(this._configService.appIdentifier).pipe(
          // If successful, dispatch success action with result
          map((payload) => ({ type: VehicleActions.GET_PRODUCT_FORM_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        )
      )
    )
  );

  decodeVin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.DECODE_VIN),
      withLatestFrom(this.store.select((state) => state.userState.user)),
      switchMap(([action, user]: [VehicleActions.DecodeVinAction, UserResponse]) =>
        this.apiService.decodeVin({ vin: action.payload, dealer: user.cmsDealerNumber }).pipe(
          // If successful, dispatch success action with result
          map((payload) => {
            payload.loadQuote = false;

            return { type: VehicleActions.DECODE_VIN_SUCCESS, payload };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: VehicleActions.DECODE_VIN_FAILURE, payload }))
        )
      )
    )
  );

  vinFailure$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.DECODE_VIN_FAILURE),
      withLatestFrom(this.store.select((state) => state.vehicleState)),
      switchMap(([, vehicleState]: [VehicleActions.DecodeVinFailureAction, State]) =>
        this.apiService.manualDecode().pipe(
          // If successful, dispatch success action with result
          map((payload: ManualResponse) => {
            const vinManual = new DecodeVinResponse();
            vinManual.year = payload.year;

            return { type: VehicleActions.DECODE_VIN_MANUAL, payload };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => {
            const vinError = new DecodeVinResponse();
            vinError.vin = vehicleState.vin.vin;

            return of({ type: VehicleActions.VEHICLE_FAILURE, payload });
          })
        )
      )
    )
  );

  getMake$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_MAKE),
      withLatestFrom(this.store.select((state) => state.vehicleState.vin)),
      switchMap(([action, vinData]: [VehicleActions.GetMakeAction, DecodeVinResponse]) =>
        this.apiService.manualDecode(action.payload.year).pipe(
          // If successful, dispatch success action with result
          map((response: ManualResponse) => {
            vinData.make = response.make;

            return { type: VehicleActions.DECODE_VIN_MANUAL, payload: vinData };
          }),
          // If request fails, dispatch failed action
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        )
      )
    )
  );

  getModel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_MODEL),
      withLatestFrom(this.store.select((state) => state.vehicleState.vin)),
      switchMap(([action, vinData]: [VehicleActions.GetModelAction, DecodeVinResponse]) =>
        this.apiService.manualDecode(action.payload.year, action.payload.make).pipe(
          // If successful, dispatch success action with result
          map((response: ManualResponse) => {
            vinData.model = response.model;

            return { type: VehicleActions.DECODE_VIN_MANUAL, payload: vinData };
          }),
          // If request fails, dispatch failed action
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        )
      )
    )
  );

  getTrim$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_TRIM),
      withLatestFrom(this.store.select((state) => state.vehicleState.vin)),
      switchMap(([action, vinData]: [VehicleActions.GetTrimAction, DecodeVinResponse]) => {
        const { year, make, model } = action.payload;

        return this.apiService.manualDecode(year, make, model).pipe(
          // If successful, dispatch success action with result

          map((response: ManualResponse) => {
            vinData.trim = response.trim;

            return { type: VehicleActions.DECODE_VIN_MANUAL, payload: vinData };
          }),
          // If request fails, dispatch failed action
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        );
      })
    )
  );

  getCustomerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_CUSTOMER_POSTAL),
      switchMap((action: VehicleActions.GetCustomerPostalAction) =>
        this.apiService.getPostal({ postalcode: action.payload.zipCode }).pipe(
          // If successful, dispatch success action with result
          map((payload: GetPostalCodeResponse) => ({ type: VehicleActions.GET_CUSTOMER_POSTAL_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: VehicleActions.VEHICLE_FAILURE, payload: {} }))
        )
      )
    )
  );
}
